/**
 * React hooks and components for tracking profile list views
 *
 * @example Component usage (declarative)
 * <ProfileListViewTracker
 *   entityType="Provider"
 *   listType="Referral Results"
 *   listItems={[{hgProfileKey: "123", position: 0}]}
 * >
 *   <YourResultsList />
 * </ProfileListViewTracker>
 *
 * @example Hook usage
 * const trackProfileListView = useTrackProfileListView();
 *
 * useEffect(() => {
 *   trackProfileListView({
 *     entityType: 'Provider',
 *     listType: 'Referral Results',
 *     listItems: results.map((item, index) => ({
 *       hgProfileKey: item.profileKey,
 *       position: index,
 *       customProperties: [
 *         { key: 'pesRating', value: item.rating.toString() }
 *       ]
 *     }))
 *   });
 * }, [results]);
 */

import React, { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useCohesion } from '../../CohesionProvider';
import ProfileListViewedEvent from './index';

const { createEventPayload } = ProfileListViewedEvent;

/**
 * @typedef {import('zod').infer<
 *   typeof import('./index').default.ReactComponentInputSchema
 * >} ProfileListViewedEventSchema
 */

// NOTE: Deduplication is handled by CohesionProvider (deduplication: { scope: 'page' } in event definition)

/**
 * Hook for tracking profile list views
 * @param {ProfileListViewedEventSchema} eventData - Event data
 * @returns {React.RefCallback<HTMLDivElement>} The ref to attach to your element to observe its visibility
 */
export function useTrackProfileListView(eventData) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1, // 10% of the element must be in the viewport
  });

  const { trackEvent } = useCohesion();

  const latestEventDataRef = useRef(eventData);
  useEffect(() => {
    latestEventDataRef.current = eventData;
  });

  useEffect(() => {
    if (!inView) return;

    trackEvent(createEventPayload(latestEventDataRef.current));
  }, [inView, trackEvent]);

  return ref;
}

/**
 * Component wrapper for tracking profile list views when children become visible
 *
 * @param {ProfileListViewedEventSchema & {
 *   as?: React.ElementType, // Component to render as wrapper
 *   children: React.ReactNode,
 * }} props
 */
export function ProfileListViewTracker({
  children,
  as: Element = 'div',
  ...trackingProps
}) {
  const ref = useTrackProfileListView(trackingProps);

  return (
    <Element
      data-testid="profile-list-view-tracker"
      /**
       * If `children` is not provided, the ref will not be attached to the element
       * and the "in view" state will not be tracked.
       * This is useful for conditionally-rendered elements like Modals
       */
      ref={children ? ref : null}
    >
      {children}
    </Element>
  );
}
